import React, { useEffect, useState } from 'react';
import { Form, Button, ListGroup, Card } from 'react-bootstrap';
import { apiFetch } from '../utils/api'; // custom fetch wrapper
import { useGlobalState } from '../contexts/GlobalContext';
import { isRealtor } from '../utils/userAccess';

function BulletinBoard() {
  const { state } = useGlobalState();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const userId = state.user?.id;
  const teamId = state.user?.team?.id || null;
  const realtorView = isRealtor(state.user);
  const boardTitle = realtorView ? 'Brokerage Message Board' : 'Team Message Board';
  const boardAudience = realtorView ? 'your brokerage' : 'your team';

  const fetchMessages = async () => {
    if (!teamId) return;
    try {
      const data = await apiFetch('messages', {
        queryParams: { team_id: teamId },
      });
      const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days in ms
      const recentOnly = Array.isArray(data)
        ? data.filter((msg) => {
            const created = msg?.created_at
              ? new Date(String(msg.created_at).replace(' ', 'T')).getTime()
              : NaN;
            return !Number.isNaN(created) && created >= cutoff;
          })
        : [];
      setMessages(recentOnly);
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load messages');
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

  try {
      await apiFetch('messages', {
        method: 'POST',
        body: JSON.stringify({
          message: newMessage,
          user_id: userId,
          team_id: teamId,
        }),
      });
      setNewMessage('');
      await fetchMessages();
    } catch (err) {
      setError(err.message);// reload
    }
  };

  const startEdit = (msg) => {
    setEditingId(msg.id);
    setEditText(msg.message);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async () => {
    if (!editingId || !editText.trim()) return;
    try {
      await apiFetch('messages', {
        method: 'PUT',
        body: JSON.stringify({ message_id: editingId, message: editText.trim() }),
      });
      setEditingId(null);
      setEditText('');
      await fetchMessages();
    } catch (err) {
      setError(err.message || 'Failed to update message');
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await apiFetch('messages', {
        method: 'DELETE',
        queryParams: { message_id: id },
      });
      await fetchMessages();
    } catch (err) {
      setError(err.message || 'Failed to delete message');
    }
  };

  useEffect(() => {
       if (teamId) fetchMessages();
  }, [teamId]);

  if (!teamId) {
    return (
      <Card className="mt-4">
        <Card.Header>
          <strong>{boardTitle}</strong>
        </Card.Header>
        <Card.Body>
          <div className="text-muted">
            {realtorView
              ? 'Join a brokerage from Account to use the shared bulletin board.'
              : 'Create or join a business from Account to use the shared team board.'}
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
       <Card.Header>
  <strong>{boardTitle}</strong>
  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
    Messages are visible to {boardAudience} for 30 days.
  </div>
</Card.Header>

      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group controlId="message">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder={`Post a message to ${boardAudience}...`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
          </Form.Group>
          <Button variant="primary" type="submit" className="mt-2" disabled={!teamId || !userId}>
            Post Message
          </Button>
        </Form>
        {error && <div className="text-danger mt-2">{error}</div>}
        <ListGroup className="mt-4">
          {messages.length === 0 && !error && (
            <ListGroup.Item className="text-muted">No messages yet.</ListGroup.Item>
          )}
          {messages.map((msg) => {
            let dateStr = msg.created_at;
            try {
              const d = new Date(msg.created_at.replace(' ', 'T'));
              if (!isNaN(d.getTime())) dateStr = d.toLocaleString();
            } catch (_) {}
            const isAuthor = Number(msg.user_id) === Number(userId);
            return (
              <ListGroup.Item key={msg.id}>
                <div className="d-flex justify-content-between align-items-start">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong>
                      {msg.first_name} {msg.last_name}
                    </strong>
                    {editingId === msg.id ? (
                      <div className="mt-2">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                        />
                        <div className="mt-2">
                          <Button size="sm" variant="success" className="me-2" onClick={saveEdit}>Save</Button>
                          <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <span>: {msg.message}</span>
                    )}
                    <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {dateStr}
                    </div>
                  </div>
                  {isAuthor && editingId !== msg.id && (
                    <div className="ms-3 d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => startEdit(msg)}>Edit</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => deleteMessage(msg.id)}>Delete</Button>
                    </div>
                  )}
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </Card.Body>
    </Card>
  );
}

export default BulletinBoard;
